import moment from "moment";
import LostDeal from "../../../../mainDeals/deals/deal/lost-deal/lost-form-deal";
import DealQuickView from "../../../../mainDeals/deals/deal/popup";
import WonDeal from "../../../../mainDeals/deals/deal/won-deal/won-form-deal";
import EmployeeDirectoryAddUpdate from "../../../../organization/emplyee-directory/add-update";
import {
  Calendar,
  Check,
  Clock,
  Copy,
  CopyCheck,
  Pencil,
  Settings2,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  User,
  XCircle,
} from "lucide-react";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import Swal from "sweetalert2";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import { Suspense, useState } from "react";
import Style from "../style.module.css";
import CustomizeTabsOverviewPopup from "./customize-tabs-popup";
import { useMemo } from "react";
import FilesTab from "../../../../../component/files";
import CommentsSection from "../../../../../component/comment";
import ProjectQuickView from "../../../../projects/project/popup";

const CopyIcon = ({ text }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <>
      <div
        className="ms-2 hover:opacity-50 cursor-pointer transition-all"
        onClick={() => {
          copyToClipboard(text);
        }}
      >
        {isCopied ? (
          <CopyCheck size={15} color="#333" />
        ) : (
          <Copy size={15} color="#333" />
        )}
      </div>
    </>
  );
};

function NewField({ label, value, icon: Icon }) {
  const [isActive, setIsActive] = useState(false);
  const isUrl =
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"));
  return (
    <div>
      {isUrl ? (
        <>
          <div className="flex flex-col gap-y-[10px]">
            <div className="flex gap-[10px] items-center">
              {Icon ? (
                <span>{Icon ? <Icon size={16} /> : <User size={16} />} </span>
              ) : null}
              <p className="text-xs font-medium">{label}</p>
            </div>

            <div className="flex justify-between text-[14px] w-full font-normal text-[#999]">
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className=" text-gray-500 hover:underline "
              >
                {value?.length > 100 ? `${value?.slice(0, 50)}...` : value}
              </a>

              <CopyIcon text={value} />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-y-[10px]">
          <div className="flex gap-[10px] items-center">
            {Icon ? (
              <span>{Icon ? <Icon size={16} /> : <User size={16} />} </span>
            ) : null}
            <p className="text-xs font-medium">{label}</p>
          </div>

          <div className="text-sm font-medium text-[rgba(14,18,27,0.40)]">
            {value}
          </div>
        </div>
      )}
    </div>
  );
}

const Project = ({
  leadDetail,
  DealStages = [],
  onSuccess = () => {},
  onError = () => {},
  activeProjectData = {},
}) => {
  const [projPopup, setProjPopup] = useState(false);
  const [projData, setProjData] = useState({});

  const metas = Object.fromEntries(
    activeProjectData?.metas?.map((m) => [m.meta_key, m.meta_value]) ?? []
  );

  const campaignKeys = [
    "parent_url",
    "user_agent",
    "campaigned",
    "is_dropoff",
    "adgroupid",
    "keyword",
    "device",
    "matchtype",
    "msclkid",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
  ];

  const excludeKeys = [
    "name",
    "email",
    "phone",
    "lead_source",
    "leadstatus",
    "leadtime",
    "country",
    "date",
  ];

  const campaignMeta = activeProjectData?.metas?.filter((m) =>
    campaignKeys.includes(m.meta_key)
  );

  const otherMeta = activeProjectData?.metas?.filter(
    (m) =>
      !campaignKeys.includes(m.meta_key) && !excludeKeys.includes(m.meta_key)
  );
  return (
    <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
      <div className="flex flex-col gap-y-[17px]">
        {/* Project DETAILS */}
        <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5">
          <div className="flex flex-col gap-y-[10px]">
            <div className="flex items-center justify-between">
              <h2 className="text-[#333333] text-[20px]">Project Details</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
                <NewField
                  label="Name"
                  value={activeProjectData?.customer_details?.name}
                />

                <NewField
                  label="Email"
                  value={activeProjectData?.customer_details?.email}
                />

                <NewField
                  label="Phone"
                  value={activeProjectData?.customer_details?.phone}
                />
              </div>

              <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
                <NewField
                  label="Invoice Id"
                  value={activeProjectData?.invoiceid}
                />
                <NewField
                  label="Project Cost"
                  value={activeProjectData?.projectcost}
                />

                <NewField
                  label="Project Key"
                  value={activeProjectData?.projectkey}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <NewField
                label="Start Date"
                value={activeProjectData?.startdate}
              />

              <button
                onClick={() => {
                  setProjPopup(true);
                  setProjData(activeProjectData);
                }}
                className="rounded-full border-2 border-black px-6 py-3 text-sm font-medium hover:bg-slate-100 transition-all"
              >
                Complete Details
              </button>
            </div>
          </div>
        </div>

        {/* Agent Details */}

        <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[#333333] text-[20px]">Agent Details</h2>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
              <NewField
                label="Name"
                value={activeProjectData?.agent_details?.name}
              />

              <NewField
                label="Email"
                value={activeProjectData?.agent_details?.email}
              />

              <NewField
                label="Phone"
                value={activeProjectData?.agent_details?.phone}
              />
            </div>

            <div className="py-[17px] flex flex-col gap-y-[34px] col-span-1">
              <NewField
                label="Page Source"
                value={activeProjectData?.agent_details?.page_source}
              />
              <NewField
                label="Source"
                value={activeProjectData?.agent_details?.source}
              />
              <NewField
                label="Created At"
                value={activeProjectData?.agent_details?.signupdate}
              />
            </div>
          </div>
        </div>

        {projPopup && (
          <ProjectQuickView
            onClose={() => {
              setProjPopup(false);
              setProjData({});
            }}
            data={projData}
          />
        )}
      </div>
    </div>
  );
};

const FollowUpsTab = ({
  leadDetail,
  onSuccess = () => {},
  onError = () => {},
  activeProjectData = {},
}) => {
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto">
        <div className="bg-white rounded-[10px] p-5  border border-[#e1e4ea]  flex flex-col gap-[10px]">
          {/* ✅ Activity Cards */}
          {activeProjectData?.schedules?.length ? (
            <div className="space-y-3">
              {activeProjectData.schedules.map((sch, i) => {
                const isCompleted = sch.status?.toLowerCase() === "completed";
                const isPending = sch.status?.toLowerCase() === "pending";
                const isHigh = sch.priority?.toLowerCase() === "high";

                const typeColors = {
                  meeting: "bg-blue-100 text-blue-700",
                  call: "bg-emerald-100 text-emerald-700",
                  email: "bg-indigo-100 text-indigo-700",
                  task: "bg-amber-100 text-amber-700",
                };
                const typeBadge =
                  typeColors[sch.type?.toLowerCase()] ||
                  "bg-gray-100 text-gray-600";

                return (
                  <div
                    key={i}
                    className="relative group rounded-xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-lg transition-all duration-200"
                  >
                    {/* ✏️ Edit button — appears on hover only */}
                    <button
                      onClick={() => {
                        setSelectedActivity(sch);
                        setShowActivityModal(true);
                      }}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 bg-white/70 hover:bg-white text-slate-600 hover:text-slate-800 p-1 rounded-full shadow-sm transition-all"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>

                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-slate-800">
                          {sch.subject}
                        </h3>

                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`px-2 py-0.5 text-[11px] font-medium rounded-full capitalize ${typeBadge}`}
                          >
                            {sch.type}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            Priority:{" "}
                            <span
                              className={`font-semibold ${
                                isHigh ? "text-red-500" : "text-emerald-600"
                              }`}
                            >
                              {sch.priority}
                            </span>
                          </span>
                        </div>
                      </div>

                      <span
                        className={`px-2 py-0.5 text-[11px] font-medium rounded-full capitalize mt-1 ml-2 ${
                          isCompleted
                            ? "bg-emerald-100 text-emerald-700"
                            : isPending
                            ? "bg-amber-100 text-amber-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {sch.status}
                      </span>
                    </div>

                    {/* Note Section */}
                    {sch.note && (
                      <p className="mt-2 text-sm text-slate-700 border-l-2 border-emerald-400 pl-2 leading-relaxed">
                        {sch.note}
                      </p>
                    )}

                    {/* Footer */}
                    <div className="mt-3 flex flex-wrap justify-between items-center text-[12px] text-gray-500 gap-2 border-t border-slate-100 pt-2">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-slate-400" />
                        <span>{sch.due_date}</span>
                        <Clock className="h-3.5 w-3.5 text-slate-400 ml-2" />
                        <span>{sch.due_time}</span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">
                          Created by <b>{sch.user?.name || "—"}</b>
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        <span>
                          Assigned to <b>{sch.assignee?.name || "—"}</b>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-sm italic">
              No scheduled activities.
            </p>
          )}

          {/* ✅ Modal Integration */}
          {showActivityModal && (
            <EmployeeDirectoryAddUpdate
              onClose={() => {
                setShowActivityModal(false);
                setSelectedActivity(null);
              }}
              data={selectedActivity}
              tabIndex={2}
              onError={onError}
              onSubmit={onSuccess}
              id={leadDetail?.dealid}
              entitytype="deal"
              entityname={leadDetail.name}
            />
          )}
        </div>
      </div>
    </>
  );
};

const Timeline = ({
  leadDetail,
  onSuccess = () => {},
  onError = () => {},
  activeProjectData = {},
}) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
        {/* Project Activity */}

        <div className="bg-white rounded-[10px] border border-[#e1e4ea] p-5 flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[#333333] text-[20px]">Project Activity</h2>
          </div>

          <div className="flex flex-col h-full mt-5">
            <div className="flex-1 overflow-y-auto pr-4">
              <div className="relative ml-1">
                {/* ✅ Single clean vertical line */}

                {activeProjectData?.activities?.length ? (
                  <>
                    <div className="absolute left-3 top-0 h-full border-l-2 border-gray-200"></div>
                    {activeProjectData?.activities?.map((act, i) => {
                      const userName = act?.user?.name || "U";
                      const userInitial = userName.charAt(0).toUpperCase();
                      const colors = [
                        "bg-emerald-500",
                        "bg-blue-500",
                        "bg-indigo-500",
                        "bg-rose-500",
                      ];
                      const userColor = "bg-slate-200";

                      return (
                        <div key={i} className="relative pl-10 pb-6">
                          {/* ✅ User Initial Circle */}
                          <div
                            className={`absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-700 text-xs font-semibold`}
                          >
                            {userInitial}
                          </div>

                          {/* ✅ Activity content properly aligned */}
                          <div className="flex flex-col gap-1 pt-0.5">
                            <div className="flex justify-between items-center">
                              <p className="text-sm font-medium text-gray-800">
                                {act.log}
                              </p>
                              <p className="text-xs text-gray-400">
                                {act.logtime}
                              </p>
                            </div>

                            {act?.user?.name && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <User className="h-3 w-3 text-gray-400" />
                                <span className="leading-none">
                                  by {act.user.name}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <p className="text-gray-400">
                    No Project acitivity available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Comments = ({ activeProjectData }) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto relative">
        <div className="space-y-4 h-full">
          <CommentsSection
            type="project"
            id={activeProjectData.projectid}
            apiBaseUrl="project"
            title="true"
          />
        </div>
      </div>
    </>
  );
};
const Files = ({ activeProjectData }) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto">
        <FilesTab
          leadId={activeProjectData?.projectid}
          attachments={activeProjectData?.attachments}
          attachmenttype="project"
        />
      </div>
    </>
  );
};

const Brief = ({ activeProjectData }) => {
  return (
    <>
      <div className="flex flex-col gap-[10px]  w-full h-full overflow-auto ">
        <div className="bg-white rounded-[10px] p-5  border border-[#e1e4ea]  flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[#333333] text-[20px]">Project Brief</h2>
          </div>
          {Array.isArray(activeProjectData?.briefs) &&
          activeProjectData.briefs.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {activeProjectData.briefs.map((item, index) => {
                // 🧠 Replace all URLs in answer with clickable links showing clean names
                const formattedAnswer = item.answer
                  ? item.answer.replace(/(https?:\/\/[^\s,]+)/g, (url) => {
                      try {
                        const parts = url.split("/");
                        const fileName = parts[parts.length - 1];

                        // 🧩 Check if it looks like a file link
                        const isFile = /\.[a-zA-Z0-9]{2,5}($|\?)/.test(
                          fileName
                        );

                        let displayText;
                        if (isFile) {
                          // Example: Trina-Sandell---2025Resume.pdf
                          displayText = decodeURIComponent(fileName);
                        } else {
                          // Example: linkedin.com/in/trina-sandell-2b040225
                          const domain = new URL(url).hostname.replace(
                            "www.",
                            ""
                          );
                          const path = new URL(url).pathname.replace(
                            /^\/|\/$/g,
                            ""
                          );
                          displayText = `${domain}/${path}`.slice(0, 60); // Trim long paths
                        }

                        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">${displayText}</a>`;
                      } catch {
                        return url;
                      }
                    })
                  : "—";

                // Add space between multiple links for readability
                const spacedAnswer = formattedAnswer.replace(
                  /<\/a>\s*(?=<a )/g,
                  "</a> &nbsp;&nbsp;"
                );

                return (
                  <div key={index} className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4">
                      <div className="sm:w-1/3">
                        <h3 className="text-sm font-semibold text-slate-700">
                          {item.question}
                        </h3>
                      </div>
                      <div className="sm:flex-1">
                        <p
                          className="text-sm text-slate-600 leading-relaxed whitespace-pre-line break-words"
                          dangerouslySetInnerHTML={{ __html: spacedAnswer }}
                        ></p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 italic text-sm">
              No brief data available.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

const ProjectTab = ({
  leadDetail,
  onSuccess = () => {},
  onError = () => {},
  loading = () => {},
  activeProjectData = {},
}) => {
  const TAB_CONFIG = [
    {
      key: "project",
      label: "Project",
      component: (
        <Project
          activeProjectData={activeProjectData}
          loading={loading}
          onSuccess={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "followups",
      label: "Follow ups",
      component: (
        <FollowUpsTab
          leadDetail={leadDetail}
          activeProjectData={activeProjectData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "timeline",
      label: "Timeline",
      component: (
        <Timeline
          activeProjectData={activeProjectData}
          loading={loading}
          onSuccess={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "comments",
      label: "Comments",
      component: (
        <Comments
          leadDetail={leadDetail}
          activeProjectData={activeProjectData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "files",
      label: "Files",
      component: (
        <Files
          leadId={activeProjectData?.userid}
          attachments={activeProjectData?.attachments}
          attachmenttype="contact"
          activeProjectData={activeProjectData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
    {
      key: "briefs",
      label: "Briefs",
      component: (
        <Brief
          leadId={activeProjectData?.userid}
          attachments={activeProjectData?.attachments}
          attachmenttype="contact"
          activeProjectData={activeProjectData}
          onSubmit={onSuccess}
          onError={onError}
        />
      ),
    },
  ].filter(Boolean);

  const [tab, setTab] = useState(0);
  const [show, setShow] = useState(false);

  const [tabsCustomizeTabs, setTabsCustomizeTabs] = useState({
    project: true,
    followups: true,
    timeline: true,
    comments: true,
    files: true,
    briefs: true,
  });

  const handleTabChange = (index) => {
    setTab(index);
  };

  const renderedTabs = useMemo(() => {
    return TAB_CONFIG.map((tabItem, index) => {
      if (!tabsCustomizeTabs[tabItem.key]) return null;
      return (
        <li
          key={tabItem.key}
          className={`${tab === index ? Style.active : null}`}
          onClick={() => {
            if (tabItem?.component) {
              handleTabChange(index);
            }
          }}
        >
          <div className={Style.icon}></div>
          <span>{tabItem.label}</span>
        </li>
      );
    });
  }, [tab, tabsCustomizeTabs]);

  return (
    <>
      {/* <div> */}
      {loading ? (
        <div className="w-full h-[500px] flex justify-center items-center">
          <InnerLoader />
        </div>
      ) : (
        <div className="flex flex-col gap-[10px] pt-2 w-full h-full overflow-hidden">
          {/* Project Header */}

          <div>
            <div>
              <div className={Style.modal_header}>
                <div className={Style.modal_nav}>
                  <ul>{renderedTabs}</ul>
                </div>
                <div
                  className="flex gap-[2px] justify-center cursor-pointer text-[#999]"
                  onClick={() => setShow(true)}
                >
                  <Settings2 size={20} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col h-screen overflow-hidden">
            {TAB_CONFIG.map(
              (tabItem, index) =>
                //   <div>
                tab === index && (
                  <Suspense
                    key={tabItem?.key}
                    fallback={
                      <div className="tableDataLoader">
                        <div className="tableDataLoader-wrapper">
                          <div className="loader"></div>
                          <span>Data Load...</span>
                        </div>
                      </div>
                    }
                  >
                    {tabItem?.component}
                  </Suspense>
                )
                //   </div>
            )}
          </div>
        </div>
      )}

      {show && (
        <CustomizeTabsOverviewPopup
          data={TAB_CONFIG.map((t) => t)}
          tabs={tabsCustomizeTabs}
          onClose={() => setShow(false)}
          onToggle={(tabName) => {
            setTabsCustomizeTabs((prev) => ({
              ...prev,
              [tabName]: !prev[tabName],
            }));
          }}
        />
      )}
    </>
  );
};

export default ProjectTab;
